import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Calendar } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import ContactCTA from "../components/ContactCTA";
import { projects, assets } from "../data/mock";

export default function Projects() {
  const categories = useMemo(() => ["All", ...Array.from(new Set(projects.map((p) => p.category)))], []);
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? projects : projects.filter((p) => p.category === active);

  return (
    <div className="relative">
      <Header />
      <PageHero
        eyebrow="Our Work"
        title="Projects that"
        accent="move Britain."
        subtitle="From £500 emergency repairs to £8M infrastructure programmes, explore landmark bridges, sustainable footbridges, drainage works and highway schemes."
        image={assets.aerial}
        crumbs={[{ label: "Projects" }]}
      />

      <section className="py-16 lg:py-24 bg-[#08080a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {/* Filter */}
          <div className="flex flex-wrap items-center gap-2 mb-12 reveal">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                  active === c
                    ? "bg-[#F5D000] text-black border border-[#F5D000]"
                    : "bg-white/5 text-white/75 border border-white/10 hover:border-white/25 hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
            <span className="ml-auto text-xs uppercase tracking-[0.25em] text-white/45">
              {filtered.length} {filtered.length === 1 ? "project" : "projects"}
            </span>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filtered.map((p, i) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                className="proj-card reveal relative block rounded-xl overflow-hidden bg-[#101014] border border-white/5 group"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="relative w-full overflow-hidden aspect-[16/10]">
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="px-2.5 py-1 rounded-full bg-[#F5D000] text-black text-[10px] font-semibold uppercase tracking-wider">
                      {p.category}
                    </span>
                    <span className="px-2.5 py-1 rounded-full bg-black/50 backdrop-blur border border-white/15 text-white text-[10px] font-semibold uppercase tracking-wider">
                      {p.year}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur border border-white/15 flex items-center justify-center text-white transition-all duration-500 group-hover:bg-[#F5D000] group-hover:text-black group-hover:rotate-45">
                    <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
                <div className="p-6">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2">{p.type}</div>
                  <h3 className="font-display text-xl font-bold mb-3 text-white group-hover:text-[#F5D000] transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-sm text-white/65 leading-relaxed line-clamp-2 mb-4">{p.overview}</p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-white/55">
                    <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {p.location}</span>
                    <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {p.year}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />
      <Footer />
    </div>
  );
}
